package app.nestbridge.app.ui

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.VerifiedUser
import androidx.compose.material3.Icon
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.karawek.core.auth.model.OtpChannel
import com.karawek.core.auth.screen.OtpVerificationEvent
import com.karawek.core.auth.screen.OtpVerificationState
import com.karawek.core.ui.components.CoreLoadingIndicator
import com.karawek.core.ui.components.CoreOtpInput
import com.karawek.core.ui.components.CorePrimaryButton
import com.karawek.core.ui.components.CoreScaffold
import com.karawek.core.ui.components.CoreSecondaryButton
import com.karawek.core.ui.theme.CoreSpacing
import com.karawek.core.ui.theme.CoreTheme
import com.karawek.core.ui.theme.CoreTypography
import com.karawek.core.util.PhoneNumberFormatter

@Composable
fun OtpVerificationContent(
    state: OtpVerificationState,
    modifier: Modifier = Modifier,
) {
    LaunchedEffect(Unit) {
        state.eventSink(OtpVerificationEvent.OnAppear)
    }

    CoreScaffold(
        scrollable = false,
        footer = { OtpVerificationFootnotes() },
        modifier = modifier,
    ) {
        Column(
            modifier = Modifier.fillMaxSize(),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Column(
                modifier = Modifier
                    .weight(1f)
                    .fillMaxWidth(),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(CoreSpacing.md, Alignment.CenterVertically),
            ) {
                Surface(
                    shape = CircleShape,
                    color = CoreTheme.colors.surfaceVariant,
                ) {
                    Icon(
                        imageVector = Icons.Outlined.VerifiedUser,
                        contentDescription = null,
                        tint = CoreTheme.colors.onBackground,
                        modifier = Modifier
                            .size(56.dp)
                            .padding(CoreSpacing.sm),
                    )
                }

                Text(
                    text = "Enter verification code",
                    style = CoreTypography.headline,
                    color = CoreTheme.colors.textPrimary,
                )

                Text(
                    text = "We sent a 6-digit code to ${formatOtpDestination(state.channel, state.destination)}",
                    style = CoreTypography.body,
                    color = CoreTheme.colors.textSecondary,
                )

                CoreOtpInput(
                    value = state.otpCode,
                    onValueChange = {
                        state.eventSink(OtpVerificationEvent.UpdateOtpCode(it))
                    },
                    length = 6,
                    onComplete = {
                        state.eventSink(OtpVerificationEvent.Verify)
                    },
                )

                state.errorMessage?.let {
                    Text(
                        text = it,
                        color = CoreTheme.colors.error,
                        style = CoreTypography.caption,
                        fontWeight = FontWeight.SemiBold,
                    )
                }

                if (state.isLoading) {
                    CoreLoadingIndicator()
                } else {
                    Column(
                        verticalArrangement = Arrangement.spacedBy(CoreSpacing.xs),
                    ) {
                        CorePrimaryButton(
                            text = "Verify & continue",
                            onClick = {
                                state.eventSink(OtpVerificationEvent.Verify)
                            },
                            modifier = Modifier.fillMaxWidth(),
                            enabled = state.otpCode.length >= 6,
                        )

                        CoreSecondaryButton(
                            text = "Cancel",
                            onClick = {
                                state.eventSink(OtpVerificationEvent.Back)
                            },
                            modifier = Modifier.fillMaxWidth(),
                        )
                    }
                }

                val resendEnabled = state.canResend && !state.isLoading
                Text(
                    text = when {
                        state.isLockedOut -> "Too many attempts. Resend unavailable."
                        state.resendCountdownSeconds > 0 -> "Resend in ${formatOtpCountdown(state.resendCountdownSeconds)}"
                        else -> "Didn't receive a code? Resend"
                    },
                    style = CoreTypography.bodySecondary,
                    color = if (resendEnabled) CoreTheme.colors.onBackground else CoreTheme.colors.textSecondary,
                    modifier = Modifier
                        .padding(top = CoreSpacing.sm)
                        .then(
                            if (resendEnabled) {
                                Modifier.clickable { state.eventSink(OtpVerificationEvent.Resend) }
                            } else {
                                Modifier
                            },
                        ),
                )
            }
        }
    }
}

@Composable
internal fun OtpVerificationFootnotes() =
    Row(
        horizontalArrangement = Arrangement.Center,
    ) {
        Text(
            text = "Your data is encrypted and protected. We comply with HIPAA guidelines " +
                    "to keep your child's information safe.",
            style = CoreTypography.footnote,
            color = CoreTheme.colors.textSecondary,
            modifier = Modifier
                .padding(horizontal = CoreSpacing.md),
        )
    }

internal fun formatOtpCountdown(seconds: Int): String {
    val minutes = seconds / 60
    val secs = seconds % 60
    return when {
        minutes > 0 && secs > 0 -> "${minutes}m ${secs}s"
        minutes > 0 -> "${minutes}m"
        else -> "${secs}s"
    }
}

internal fun formatOtpDestination(channel: OtpChannel, destination: String): String =
    when (channel) {
        OtpChannel.Phone -> PhoneNumberFormatter.toDisplayFormat(destination)
        OtpChannel.Email -> destination
        OtpChannel.WhatsApp -> PhoneNumberFormatter.toDisplayFormat(destination)
    }
